/**
 * RequestTypeDetailsView
 *
 * Read-only display of a Task's per-request-type payload (`Task.requestTypeDetails`).
 * This is a thin DISPATCHER: it resolves the request type's registry definition and
 * renders the type's custom `DetailsView` (e.g. IO) if it has one, otherwise the generic
 * declarative `DynamicRequestDetails` for `fields`-based types. Returns null for request
 * types with no extra details, so it is safe to drop in unconditionally.
 *
 * Rendered by the drawer, details metadata, and mobile sheet.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getRequestTypeDef, DynamicRequestDetails } from '../forms/requestTypes';

const parseDetails = (raw) => {
  // requestTypeDetails is a Json scalar; tolerate both an already-parsed object and a string.
  let details = raw;
  if (typeof details === 'string') {
    try {
      details = JSON.parse(details);
    } catch (e) {
      details = null;
    }
  }
  return details || null;
};

const RequestTypeDetailsView = ({ task, hideNote }) => {
  if (!task) return null;

  const def = getRequestTypeDef(task.requestType);
  if (!def) return null;

  const details = parseDetails(task.requestTypeDetails);
  if (!details) return null;

  // Custom read view (e.g. IO) takes precedence; otherwise render declarative `fields`.
  if (def.DetailsView) {
    const DetailsView = def.DetailsView;
    return <DetailsView details={details} task={task} hideNote={hideNote} />;
  }
  if (def.fields) {
    return (
      <DynamicRequestDetails
        fields={def.fields}
        details={details}
        title={def.detailsTitle || 'REQUEST DETAILS'}
      />
    );
  }
  return null;
};

RequestTypeDetailsView.propTypes = {
  task: PropTypes.shape({
    requestType: PropTypes.string,
    requestTypeDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }),
  // When true, omit the Note row — used where an editable Description/Note block renders the
  // note instead (the drawer + full-page details), so it isn't shown twice. Honored by custom
  // DetailsViews that have a note (e.g. IO).
  hideNote: PropTypes.bool,
};

RequestTypeDetailsView.defaultProps = {
  task: null,
  hideNote: false,
};

export default RequestTypeDetailsView;
